// ─── TransactionGrid.tsx ──────────────────────────────────────────────────────
// Editable transaction grid with status-based tabs for the CC Recon module.
//
// Responsibilities:
//   - Cross-tab summary table (Selected / Not Selected / Total × Purchases / Returns)
//   - Checkbox selection for eligible (Coded/Corrected) rows
//   - "Convert Selected" toolbar button (bulk conversion of checked rows)
//   - Tab bar with per-status counts (hides tabs with 0 rows)
//   - Inline editing of Vendor#, GL#, Job# (saves on blur via PATCH endpoint)
//   - Row highlighting for Unresolved (yellow #fff8e1), selected rows (blue #ebf3ff)
//   - Status + resolution source badges
//   - Per-row Retry (Error rows) and Review (Possible Match rows) buttons
//   - Internal toast for inline-edit save failures

import React, { useState, useEffect, useCallback } from 'react';
import {
  CCReconTransaction,
  CCReconSession,
  CCTransactionStatus,
  CCTransactionUpdate,
  CC_BLOCKED_STATUSES,
  CC_LOCKED_STATUSES,
  CC_UNRESOLVED_COLOR,
  CC_UNRESOLVED_GL,
} from '../../types/ccTypes';
import { updateTransaction } from '../../api/ccReconApi';
import { getGLAccounts, getJobs, searchVendors } from '../../api';
import { GLAccount, Job } from '../../types';

// ── Tab definition ────────────────────────────────────────────────────────────

const TABS: Array<CCTransactionStatus | 'All'> = [
  'Unresolved',
  'Coded',
  'Corrected',
  'Possible Match',
  'Confirmed Match',
  'Converted',
  'Duplicate',
  'Error',
  'All',
];

// ── Badge colour maps ─────────────────────────────────────────────────────────

const STATUS_STYLE: Record<string, { bg: string; color: string }> = {
  'Unresolved':      { bg: '#fff8e1', color: '#b8860b' },
  'Coded':           { bg: '#e3f2fd', color: '#1565c0' },
  'Corrected':       { bg: '#e8f5e9', color: '#2d7a2d' },
  'Possible Match':  { bg: '#fff3e0', color: '#e65100' },
  'Confirmed Match': { bg: '#e8f5e9', color: '#1b5e20' },
  'Converted':       { bg: '#e8f5e9', color: '#1b5e20' },
  'Duplicate':       { bg: '#f3e5f5', color: '#6a1b9a' },
  'Error':           { bg: '#fdecea', color: '#c0392b' },
};

const SOURCE_STYLE: Record<string, { bg: string; color: string }> = {
  'CC Helper':    { bg: '#e3f2fd', color: '#1565c0' },
  'CC Keyword':   { bg: '#e3f2fd', color: '#0d47a1' }, // Session 56: contains match
  'Fuzzy Helper': { bg: '#e3f2fd', color: '#0d47a1' }, // Legacy — keep for existing DB rows
  'Vendor Match': { bg: '#e0f2f1', color: '#00695c' },
  'GL Keyword':   { bg: '#e0f2f1', color: '#004d40' },
  'AI Assist':    { bg: '#f3e5f5', color: '#6a1b9a' },
  'Unresolved':   { bg: '#f5f5f5', color: '#757575' },
  'Manual':       { bg: '#fff3e0', color: '#e65100' },
};

// ── Draft tracking (one entry per transaction ID) ─────────────────────────────

interface RowDraft {
  vendor: string;  // string so input stays controlled during typing
  gl: string;
  job: string;
  keyword: string; // AI-suggested or user-edited keyword for future matching
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface StatusModalState {
  txnId: number;
  currentStatus: CCTransactionStatus;
  selectedStatus: CCTransactionStatus;
  notes: string;
  saving: boolean;
}

interface Props {
  session: CCReconSession;
  transactions: CCReconTransaction[];
  onTransactionUpdated: (updated: CCReconTransaction) => void;
  /** Called for per-row Retry (Error rows only). Should not throw — parent shows toast. */
  onConvertRow: (transactionId: number) => Promise<void>;
  convertingIds: Set<number>;
  /** Called with array of selected eligible IDs for bulk conversion. Should not throw. */
  onConvertSelected: (ids: number[]) => Promise<void>;
  convertingSelected: boolean;
  /** Open the Possible Match review panel for this transaction. */
  onReviewMatch: (transactionId: number) => void;
  /** Manual match — user enters a WinTeam invoice number to confirm match. */
  onManualMatch: (transactionId: number) => void;
  /** Admin-only: force any status on any transaction. */
  isAdmin?: boolean;
  onForceStatus?: (transactionId: number, status: CCTransactionStatus, notes?: string) => Promise<void>;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${m}/${d}/${y.slice(2)}`;
}

function fmtAmt(n: number): string {
  return `$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function sumAmounts(txns: CCReconTransaction[]): number {
  return txns.reduce((acc, t) => acc + Number(t.invoice_amount), 0);
}

// ── GridTypeaheadCell ──────────────────────────────────────────────────────────
// Compact typeahead for use inside table cells.
// - Pass `options` for a preloaded static list (GL, Job).
// - Pass `onSearch` for async search (Vendor).
// - `onSelect` is called immediately on option click — saves without waiting for blur.
// - `onChange` / `onBlur` still work for typed values, preserving existing save logic.

interface GridOption { value: string; label: string; }

interface GridTypeaheadCellProps {
  value: string;
  options?: GridOption[];
  onSearch?: (q: string) => Promise<GridOption[]>;
  onChange: (v: string) => void;
  onBlur: () => void;
  onSelect: (v: string) => void;
  disabled?: boolean;
  inputStyle?: React.CSSProperties;
}

function GridTypeaheadCell({
  value, options, onSearch, onChange, onBlur, onSelect, disabled, inputStyle,
}: GridTypeaheadCellProps) {
  const [open, setOpen] = useState(false);
  const [asyncResults, setAsyncResults] = useState<GridOption[]>([]);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const displayOptions = options
    ? options.filter(o =>
        !value.trim() ||
        o.value.toLowerCase().includes(value.toLowerCase()) ||
        o.label.toLowerCase().includes(value.toLowerCase())
      ).slice(0, 50)
    : asyncResults;

  const handleChange = (v: string) => {
    onChange(v);
    if (onSearch) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(async () => {
        if (v.trim().length >= 1) {
          const results = await onSearch(v.trim());
          setAsyncResults(results);
        } else {
          setAsyncResults([]);
        }
      }, 250);
    }
    setOpen(true);
  };

  return (
    <div style={{ position: 'relative' }}>
      <input
        type="text"
        value={value}
        onChange={e => handleChange(e.target.value)}
        onFocus={() => setOpen(true)}
        onBlur={() => { setTimeout(() => { setOpen(false); }, 150); onBlur(); }}
        disabled={disabled}
        style={inputStyle}
        autoComplete="off"
      />
      {open && displayOptions.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 2000,
          minWidth: '280px', maxHeight: '200px', overflowY: 'auto',
          backgroundColor: 'white', border: '1px solid #cbd5e0',
          borderRadius: '4px', boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
        }}>
          {displayOptions.map(opt => (
            <div
              key={opt.value}
              onMouseDown={() => { onSelect(opt.value); setOpen(false); }}
              style={{ padding: '6px 10px', fontSize: '12px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', whiteSpace: 'nowrap' }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f0f7ff')}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'white')}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function TransactionGrid({
  session,
  transactions,
  onTransactionUpdated,
  onConvertRow,
  convertingIds,
  onConvertSelected,
  convertingSelected,
  onReviewMatch,
  onManualMatch,
  isAdmin = false,
  onForceStatus,
}: Props) {
  const [activeTab, setActiveTab] = useState<CCTransactionStatus | 'All'>('Unresolved');
  const [drafts, setDrafts] = useState<Record<number, RowDraft>>({});
  const [savingIds, setSavingIds] = useState<Set<number>>(new Set());
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [statusModal, setStatusModal] = useState<StatusModalState | null>(null);

  // Lookup data for typeaheads
  const [glAccounts, setGLAccounts] = useState<GLAccount[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);

  useEffect(() => {
    getGLAccounts().then(setGLAccounts).catch(() => {});
    getJobs().then(setJobs).catch(() => {});
  }, []);

  // ── Initialise drafts for NEW transaction IDs only ─────────────────────────
  // Does NOT reset currently-focused inputs when parent pushes an update.
  useEffect(() => {
    setDrafts(prev => {
      const next = { ...prev };
      transactions.forEach(t => {
        if (!(t.id in next)) {
          next[t.id] = {
            vendor: String(t.vendor_number),
            gl: String(t.gl_number),
            job: t.job_number,
            keyword: t.suggested_keyword || '',
          };
        }
      });
      return next;
    });
  }, [transactions]);

  // ── Prune selectedIds when rows become locked/blocked ─────────────────────
  // e.g. after a row is converted it moves to Converted status → remove from selection
  useEffect(() => {
    setSelectedIds(prev => {
      const next = new Set<number>();
      prev.forEach(id => {
        const txn = transactions.find(t => t.id === id);
        if (txn && !CC_BLOCKED_STATUSES.includes(txn.status)) {
          next.add(id);
        }
      });
      // Return same reference if nothing changed to avoid re-render
      if (next.size === prev.size && Array.from(prev).every(id => next.has(id))) return prev;
      return next;
    });
  }, [transactions, session.status]);

  const showToast = (msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3500);
  };

  // ── Tab counts (derived) ──────────────────────────────────────────────────
  const tabCounts: Record<string, number> = { All: transactions.length };
  TABS.forEach(tab => {
    if (tab !== 'All') {
      tabCounts[tab] = transactions.filter(t => t.status === tab).length;
    }
  });

  // Auto-switch away from empty tab when transactions change
  useEffect(() => {
    if (activeTab !== 'All' && tabCounts[activeTab] === 0) {
      setActiveTab('All');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactions]);

  const filtered =
    activeTab === 'All'
      ? transactions
      : transactions.filter(t => t.status === activeTab);

  // ── Inline edit handlers ──────────────────────────────────────────────────

  const handleFieldChange = (id: number, field: 'vendor' | 'gl' | 'job' | 'keyword', value: string) => {
    setDrafts(prev => ({
      ...prev,
      [id]: { ...prev[id], [field]: value },
    }));
  };

  const handleBlur = useCallback(
    async (txn: CCReconTransaction, field: 'vendor' | 'gl' | 'job' | 'keyword') => {
      const draft = drafts[txn.id];
      if (!draft) return;

      const changes: CCTransactionUpdate = {};
      if (field === 'vendor') {
        const v = parseInt(draft.vendor, 10);
        if (!isNaN(v) && v > 0 && v !== txn.vendor_number) changes.vendor_number = v;
      } else if (field === 'gl') {
        const g = parseInt(draft.gl, 10);
        if (!isNaN(g) && g > 0 && g !== txn.gl_number) changes.gl_number = g;
      } else if (field === 'keyword') {
        const kw = draft.keyword.trim();
        if (kw !== (txn.suggested_keyword || '')) changes.suggested_keyword = kw;
      } else {
        const j = draft.job.trim();
        if (j && j !== txn.job_number) changes.job_number = j;
      }

      if (Object.keys(changes).length === 0) return;

      setSavingIds(prev => new Set(prev).add(txn.id));
      try {
        const updated = await updateTransaction(txn.id, changes);
        // Sync draft to server-confirmed values
        setDrafts(prev => ({
          ...prev,
          [updated.id]: {
            vendor: String(updated.vendor_number),
            gl: String(updated.gl_number),
            job: updated.job_number,
            keyword: updated.suggested_keyword || '',
          },
        }));
        onTransactionUpdated(updated);
      } catch (err: any) {
        const msg =
          err?.response?.status === 409
            ? 'Row is locked — cannot edit after conversion'
            : err?.response?.data?.error || 'Failed to save change';
        showToast(msg, false);
        // Revert draft to last known-good values
        setDrafts(prev => ({
          ...prev,
          [txn.id]: {
            vendor: String(txn.vendor_number),
            gl: String(txn.gl_number),
            job: txn.job_number,
            keyword: txn.suggested_keyword || '',
          },
        }));
      } finally {
        setSavingIds(prev => {
          const s = new Set(prev);
          s.delete(txn.id);
          return s;
        });
      }
    },
    [drafts, onTransactionUpdated]
  );

  // ── Typeahead immediate save (called on option click, bypasses blur flow) ──

  const handleFieldSave = useCallback(
    async (txn: CCReconTransaction, field: 'vendor' | 'gl' | 'job', newValue: string) => {
      const changes: CCTransactionUpdate = {};
      if (field === 'vendor') {
        const v = parseInt(newValue, 10);
        if (isNaN(v) || v <= 0 || v === txn.vendor_number) return;
        changes.vendor_number = v;
      } else if (field === 'gl') {
        const g = parseInt(newValue, 10);
        if (isNaN(g) || g <= 0 || g === txn.gl_number) return;
        changes.gl_number = g;
      } else {
        const j = newValue.trim();
        if (!j || j === txn.job_number) return;
        changes.job_number = j;
      }
      if (Object.keys(changes).length === 0) return;

      // Update draft optimistically so the input shows the new value immediately
      setDrafts(prev => ({
        ...prev,
        [txn.id]: {
          ...prev[txn.id],
          vendor: field === 'vendor' ? newValue : prev[txn.id]?.vendor ?? String(txn.vendor_number),
          gl:     field === 'gl'     ? newValue : prev[txn.id]?.gl     ?? String(txn.gl_number),
          job:    field === 'job'    ? newValue : prev[txn.id]?.job    ?? txn.job_number,
        },
      }));

      setSavingIds(prev => new Set(prev).add(txn.id));
      try {
        const updated = await updateTransaction(txn.id, changes);
        setDrafts(prev => ({
          ...prev,
          [updated.id]: {
            ...prev[updated.id],
            vendor: String(updated.vendor_number),
            gl: String(updated.gl_number),
            job: updated.job_number,
            keyword: updated.suggested_keyword || '',
          },
        }));
        onTransactionUpdated(updated);
      } catch (err: any) {
        const msg =
          err?.response?.status === 409
            ? 'Row is locked — cannot edit after conversion'
            : err?.response?.data?.error || 'Failed to save change';
        showToast(msg, false);
        // Revert to last confirmed values
        setDrafts(prev => ({
          ...prev,
          [txn.id]: {
            ...prev[txn.id],
            vendor: String(txn.vendor_number),
            gl: String(txn.gl_number),
            job: txn.job_number,
          },
        }));
      } finally {
        setSavingIds(prev => { const s = new Set(prev); s.delete(txn.id); return s; });
      }
    },
    [onTransactionUpdated]
  );

  // ── Row predicates ────────────────────────────────────────────────────────

  // Locked = truly final statuses only (Converted, Confirmed Match, Duplicate).
  // Coded/Corrected rows remain editable even after session is completed.
  const isLocked = (txn: CCReconTransaction) =>
    CC_LOCKED_STATUSES.includes(txn.status);

  // Rows eligible for checkbox selection = Coded or Corrected (regardless of session status)
  const isSelectable = (txn: CCReconTransaction) =>
    !CC_BLOCKED_STATUSES.includes(txn.status);

  // ── Selection handlers ────────────────────────────────────────────────────

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectableInView = filtered.filter(isSelectable);
  const allInViewSelected =
    selectableInView.length > 0 && selectableInView.every(t => selectedIds.has(t.id));

  const toggleSelectAll = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allInViewSelected) {
        selectableInView.forEach(t => next.delete(t.id));
      } else {
        selectableInView.forEach(t => next.add(t.id));
      }
      return next;
    });
  };

  // ── Cross-tab summary table data ──────────────────────────────────────────
  // Always computed across ALL transactions (not just the filtered tab view)

  const allPurchases = transactions.filter(t => t.type === 'Sale');
  const allReturns   = transactions.filter(t => t.type === 'Return');

  const selPurchases   = allPurchases.filter(t => selectedIds.has(t.id));
  const unselPurchases = allPurchases.filter(t => !selectedIds.has(t.id));
  const selReturns     = allReturns.filter(t => selectedIds.has(t.id));
  const unselReturns   = allReturns.filter(t => !selectedIds.has(t.id));

  const allPurchAndRet = transactions.filter(t => t.type === 'Sale' || t.type === 'Return');
  const selPurchAndRet = allPurchAndRet.filter(t => selectedIds.has(t.id));
  const unselPurchAndRet = allPurchAndRet.filter(t => !selectedIds.has(t.id));

  const summary = {
    purchases: {
      selected:    sumAmounts(selPurchases),
      notSelected: sumAmounts(unselPurchases),
      total:       sumAmounts(allPurchases),
    },
    returns: {
      selected:    sumAmounts(selReturns),
      notSelected: sumAmounts(unselReturns),
      total:       sumAmounts(allReturns),
    },
    total: {
      selected:    sumAmounts(selPurchAndRet),
      notSelected: sumAmounts(unselPurchAndRet),
      total:       sumAmounts(allPurchAndRet),
    },
  };

  // ── Tab bar footer total ──────────────────────────────────────────────────
  const tabTotal = filtered.reduce((acc, t) => acc + Number(t.invoice_amount), 0);

  // ── Convert selected handler ──────────────────────────────────────────────
  const handleConvertSelected = async () => {
    const ids = Array.from(selectedIds);
    await onConvertSelected(ids);
    setSelectedIds(new Set()); // clear selection after converting
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
      overflow: 'hidden',
    }}>

      {/* ── Cross-tab summary table ──────────────────────────────────────── */}
      {transactions.length > 0 && (
        <div style={{
          padding: '14px 20px',
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#fafafa',
        }}>
          <table style={{ borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr>
                <th style={{ padding: '4px 20px 6px 0', textAlign: 'left', color: '#888', fontWeight: 600 }} />
                <th style={{
                  padding: '4px 20px 6px',
                  textAlign: 'right',
                  color: '#1565c0',
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  borderBottom: '2px solid #1565c0',
                }}>
                  Selected ({selectedIds.size})
                </th>
                <th style={{
                  padding: '4px 20px 6px',
                  textAlign: 'right',
                  color: '#555',
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  borderBottom: '2px solid #bbb',
                }}>
                  Not Selected
                </th>
                <th style={{
                  padding: '4px 20px 6px 20px',
                  textAlign: 'right',
                  color: '#1a1a1a',
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  borderBottom: '2px solid #1a1a1a',
                }}>
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {/* Purchases row */}
              <tr>
                <td style={{ padding: '5px 20px 5px 0', color: '#444', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  Purchases
                </td>
                <td style={{ padding: '5px 20px', textAlign: 'right', color: '#1a1a1a', fontFamily: 'monospace', fontWeight: 600 }}>
                  {fmtAmt(summary.purchases.selected)}
                </td>
                <td style={{ padding: '5px 20px', textAlign: 'right', color: '#666', fontFamily: 'monospace' }}>
                  {fmtAmt(summary.purchases.notSelected)}
                </td>
                <td style={{ padding: '5px 20px', textAlign: 'right', color: '#1a1a1a', fontFamily: 'monospace', fontWeight: 600 }}>
                  {fmtAmt(summary.purchases.total)}
                </td>
              </tr>
              {/* Credit Returns row */}
              <tr>
                <td style={{ padding: '5px 20px 5px 0', color: '#444', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  Credit Returns
                </td>
                <td style={{ padding: '5px 20px', textAlign: 'right', color: '#2d7a2d', fontFamily: 'monospace', fontWeight: 600 }}>
                  {fmtAmt(summary.returns.selected)}
                </td>
                <td style={{ padding: '5px 20px', textAlign: 'right', color: '#2d7a2d', fontFamily: 'monospace' }}>
                  {fmtAmt(summary.returns.notSelected)}
                </td>
                <td style={{ padding: '5px 20px', textAlign: 'right', color: '#2d7a2d', fontFamily: 'monospace', fontWeight: 600 }}>
                  {fmtAmt(summary.returns.total)}
                </td>
              </tr>
              {/* Total row */}
              <tr style={{ borderTop: '1px solid #e0e0e0' }}>
                <td style={{ padding: '6px 20px 4px 0', color: '#1a1a1a', fontWeight: 700, whiteSpace: 'nowrap' }}>
                  Total
                </td>
                <td style={{ padding: '6px 20px 4px', textAlign: 'right', color: '#1a1a1a', fontFamily: 'monospace', fontWeight: 700 }}>
                  {fmtAmt(summary.total.selected)}
                </td>
                <td style={{ padding: '6px 20px 4px', textAlign: 'right', color: '#555', fontFamily: 'monospace', fontWeight: 700 }}>
                  {fmtAmt(summary.total.notSelected)}
                </td>
                <td style={{ padding: '6px 20px 4px', textAlign: 'right', color: '#1a1a1a', fontFamily: 'monospace', fontWeight: 700 }}>
                  {fmtAmt(summary.total.total)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* ── Convert Selected toolbar (shown only when rows are checked) ──── */}
      {selectedIds.size > 0 && (
        <div style={{
          padding: '8px 16px',
          backgroundColor: '#e8f0fe',
          borderBottom: '1px solid #c5d0e8',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}>
          <span style={{ fontSize: '13px', color: '#1565c0', fontWeight: 600 }}>
            {selectedIds.size} row{selectedIds.size !== 1 ? 's' : ''} selected
          </span>
          <button
            onClick={handleConvertSelected}
            disabled={convertingSelected}
            style={{
              padding: '5px 16px',
              fontSize: '13px',
              fontWeight: 700,
              backgroundColor: convertingSelected ? '#e0e0e0' : '#00386C',
              color: convertingSelected ? '#888' : 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: convertingSelected ? 'default' : 'pointer',
            }}
          >
            {convertingSelected ? 'Converting…' : `⬆ Convert Selected (${selectedIds.size})`}
          </button>
          <button
            onClick={() => {
              const id = Array.from(selectedIds)[0];
              if (id) onManualMatch(id);
            }}
            disabled={convertingSelected || selectedIds.size !== 1}
            title={selectedIds.size !== 1 ? 'Select exactly 1 row to manual match' : 'Enter a WinTeam invoice number to confirm as matched'}
            style={{
              padding: '5px 16px',
              fontSize: '13px',
              fontWeight: 700,
              backgroundColor: (convertingSelected || selectedIds.size !== 1) ? '#e0e0e0' : '#1b5e20',
              color: (convertingSelected || selectedIds.size !== 1) ? '#888' : 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: (convertingSelected || selectedIds.size !== 1) ? 'default' : 'pointer',
            }}
          >
            ⇔ Manual Match
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            disabled={convertingSelected}
            style={{
              padding: '5px 12px',
              fontSize: '12px',
              backgroundColor: 'transparent',
              color: '#888',
              border: '1px solid #ccc',
              borderRadius: '5px',
              cursor: convertingSelected ? 'default' : 'pointer',
            }}
          >
            Clear
          </button>
        </div>
      )}

      {/* ── Tab bar ──────────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex',
        borderBottom: '1px solid #e0e0e0',
        backgroundColor: '#fafafa',
        overflowX: 'auto',
      }}>
        {TABS.map(tab => {
          const count = tabCounts[tab] ?? 0;
          // Hide tabs that have no rows (except All)
          if (tab !== 'All' && count === 0) return null;
          const isActive = activeTab === tab;
          const badgeBg =
            tab === 'Unresolved' ? '#b8860b'
            : tab === 'Error'    ? '#c0392b'
            : '#00386C';
          return (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '10px 16px',
                border: 'none',
                cursor: 'pointer',
                fontWeight: isActive ? 700 : 400,
                fontSize: '13px',
                color: isActive ? '#00386C' : '#555',
                borderBottom: isActive ? '2px solid #00386C' : '2px solid transparent',
                backgroundColor: 'transparent',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {tab}
              {count > 0 && (
                <span style={{
                  marginLeft: '6px',
                  fontSize: '11px',
                  fontWeight: 600,
                  backgroundColor: badgeBg,
                  color: 'white',
                  borderRadius: '10px',
                  padding: '1px 6px',
                }}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── Empty state ───────────────────────────────────────────────────── */}
      {filtered.length === 0 ? (
        <div style={{ padding: '48px', textAlign: 'center', color: '#999', fontSize: '14px' }}>
          No transactions in this tab.
        </div>
      ) : (
        <>
          {/* ── Scrollable table ────────────────────────────────────────── */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f5f7fa', borderBottom: '1px solid #e0e0e0' }}>
                  {/* Select-all checkbox column */}
                  <th style={{ padding: '8px 8px 8px 14px', width: '34px' }}>
                    {selectableInView.length > 0 && (
                      <input
                        type="checkbox"
                        checked={allInViewSelected}
                        onChange={toggleSelectAll}
                        title={allInViewSelected ? 'Deselect all in view' : 'Select all eligible in view'}
                        style={{ cursor: 'pointer', width: '14px', height: '14px' }}
                      />
                    )}
                  </th>
                  {['Card', 'Date', 'Description', 'Vendor #', 'GL #', 'Job #', 'Amount', 'Source', 'Status', 'Actions', 'New Keyword'].map(h => (
                    <th
                      key={h}
                      style={{
                        padding: '8px 10px',
                        textAlign: h === 'Amount' ? 'right' : 'left',
                        fontWeight: 600,
                        color: '#555',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {filtered.map(txn => {
                  const locked    = isLocked(txn);
                  const selectable = isSelectable(txn);
                  const saving    = savingIds.has(txn.id);
                  const converting = convertingIds.has(txn.id);
                  const selected  = selectedIds.has(txn.id);
                  const draft = drafts[txn.id] || {
                    vendor: String(txn.vendor_number),
                    gl: String(txn.gl_number),
                    job: txn.job_number,
                    keyword: txn.suggested_keyword || '',
                  };
                  const isUnresolved = txn.gl_number === CC_UNRESOLVED_GL && txn.status === 'Unresolved';

                  // Row background: selected (blue) > unresolved (yellow) > default
                  const rowBg = selected
                    ? '#ebf3ff'
                    : isUnresolved
                    ? CC_UNRESOLVED_COLOR
                    : undefined;

                  const statusSt = STATUS_STYLE[txn.status] || { bg: '#f5f5f5', color: '#555' };
                  const sourceSt = SOURCE_STYLE[txn.resolution_source] || { bg: '#f5f5f5', color: '#555' };

                  return (
                    <tr
                      key={txn.id}
                      style={{
                        borderBottom: '1px solid #f0f0f0',
                        backgroundColor: rowBg,
                        opacity: saving ? 0.65 : 1,
                        transition: 'opacity 0.15s, background-color 0.1s',
                      }}
                    >
                      {/* Row checkbox (only for selectable rows) */}
                      <td style={{ padding: '6px 8px 6px 14px', textAlign: 'center' }}>
                        {selectable && (
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => toggleSelect(txn.id)}
                            disabled={converting || convertingSelected}
                            style={{ cursor: 'pointer', width: '14px', height: '14px' }}
                          />
                        )}
                      </td>

                      {/* Card last4 */}
                      <td style={{ padding: '6px 10px', whiteSpace: 'nowrap', color: '#666', fontSize: '12px' }}>
                        ···{txn.card_last4}
                      </td>

                      {/* Date */}
                      <td style={{ padding: '6px 10px', whiteSpace: 'nowrap', color: '#444' }}>
                        {fmtDate(txn.transaction_date)}
                      </td>

                      {/* Description + notes */}
                      <td style={{ padding: '6px 10px', maxWidth: '240px' }}>
                        <div
                          title={txn.description}
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: '#1a1a1a',
                            fontWeight: 500,
                          }}
                        >
                          {txn.description}
                        </div>
                        {txn.notes && (
                          <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
                            {txn.notes}
                          </div>
                        )}
                      </td>

                      {/* Vendor # */}
                      <td style={{ padding: '4px 10px' }}>
                        {locked ? (
                          <span style={{ color: '#444' }}>{txn.vendor_number}</span>
                        ) : (
                          <GridTypeaheadCell
                            value={draft.vendor}
                            onSearch={async q => {
                              const results = await searchVendors(q);
                              return results.map(v => ({
                                value: String(v.vendor_number),
                                label: `${v.vendor_number} — ${v.vendor_name}`,
                              }));
                            }}
                            onChange={v => handleFieldChange(txn.id, 'vendor', v)}
                            onBlur={() => handleBlur(txn, 'vendor')}
                            onSelect={v => handleFieldSave(txn, 'vendor', v)}
                            disabled={saving}
                            inputStyle={{
                              width: '72px',
                              padding: '4px 6px',
                              border: '1px solid #cbd5e0',
                              borderRadius: '4px',
                              fontSize: '13px',
                              color: '#1a1a1a',
                            }}
                          />
                        )}
                      </td>

                      {/* GL # */}
                      <td style={{ padding: '4px 10px' }}>
                        {locked ? (
                          <span style={{ color: '#444' }}>{txn.gl_number}</span>
                        ) : (
                          <GridTypeaheadCell
                            value={draft.gl}
                            options={glAccounts.map(g => ({
                              value: g.gl_number,
                              label: `${g.gl_number} — ${g.description}`,
                            }))}
                            onChange={v => handleFieldChange(txn.id, 'gl', v)}
                            onBlur={() => handleBlur(txn, 'gl')}
                            onSelect={v => handleFieldSave(txn, 'gl', v)}
                            disabled={saving}
                            inputStyle={{
                              width: '72px',
                              padding: '4px 6px',
                              borderRadius: '4px',
                              fontSize: '13px',
                              color: '#1a1a1a',
                              border: isUnresolved ? '1px solid #b8860b' : '1px solid #cbd5e0',
                              backgroundColor: isUnresolved ? '#fffde7' : 'white',
                            }}
                          />
                        )}
                      </td>

                      {/* Job # */}
                      <td style={{ padding: '4px 10px' }}>
                        {locked ? (
                          <span style={{ color: '#444' }}>{txn.job_number}</span>
                        ) : (
                          <GridTypeaheadCell
                            value={draft.job}
                            options={jobs.map(j => ({
                              value: j.job_number,
                              label: `${j.job_number} — ${j.job_name}`,
                            }))}
                            onChange={v => handleFieldChange(txn.id, 'job', v)}
                            onBlur={() => handleBlur(txn, 'job')}
                            onSelect={v => handleFieldSave(txn, 'job', v)}
                            disabled={saving}
                            inputStyle={{
                              width: '56px',
                              padding: '4px 6px',
                              border: '1px solid #cbd5e0',
                              borderRadius: '4px',
                              fontSize: '13px',
                              color: '#1a1a1a',
                            }}
                          />
                        )}
                      </td>

                      {/* Amount */}
                      <td style={{
                        padding: '6px 10px',
                        whiteSpace: 'nowrap',
                        textAlign: 'right',
                        fontWeight: 600,
                        color: txn.type === 'Return' ? '#2d7a2d' : '#1a1a1a',
                      }}>
                        {txn.type === 'Return' && (
                          <span style={{ fontSize: '10px', marginRight: '3px', color: '#2d7a2d' }}>RET</span>
                        )}
                        {fmtAmt(txn.invoice_amount)}
                      </td>

                      {/* Resolution source badge */}
                      <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>
                        <span style={{
                          fontSize: '11px',
                          fontWeight: 600,
                          padding: '2px 7px',
                          borderRadius: '10px',
                          backgroundColor: sourceSt.bg,
                          color: sourceSt.color,
                        }}>
                          {txn.resolution_source}
                        </span>
                      </td>

                      {/* Status badge */}
                      <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>
                        <span style={{
                          fontSize: '11px',
                          fontWeight: 600,
                          padding: '2px 7px',
                          borderRadius: '10px',
                          backgroundColor: statusSt.bg,
                          color: statusSt.color,
                        }}>
                          {txn.status}
                        </span>
                      </td>

                      {/* Actions:
                          - Converted: show WinTeam invoice number
                          - Confirmed Match: show matched invoice number
                          - Error: Retry button (still per-row — not selectable)
                          - Possible Match: Review button
                          - Coded/Corrected: no button (use checkbox + Convert Selected instead)
                      */}
                      <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>
                        {txn.status === 'Converted' && txn.winteam_invoice_number ? (
                          <span style={{ fontSize: '12px', color: '#2d7a2d', fontWeight: 600 }}>
                            ✓ {txn.winteam_invoice_number}
                          </span>
                        ) : txn.status === 'Confirmed Match' && txn.matched_invoice_number ? (
                          <span style={{ fontSize: '12px', color: '#1b5e20' }}>
                            ⇔ {txn.matched_invoice_number}
                          </span>
                        ) : txn.status === 'Error' ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                            <button
                              onClick={() => onConvertRow(txn.id)}
                              disabled={converting}
                              style={{
                                padding: '4px 10px',
                                fontSize: '12px',
                                fontWeight: 600,
                                backgroundColor: converting ? '#e0e0e0' : '#c0392b',
                                color: converting ? '#888' : 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: converting ? 'default' : 'pointer',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {converting ? '…' : 'Retry'}
                            </button>
                            {txn.notes && (
                              <span
                                title={txn.notes}
                                style={{
                                  fontSize: '11px',
                                  color: '#c0392b',
                                  maxWidth: '220px',
                                  whiteSpace: 'normal',
                                  lineHeight: '1.3',
                                  wordBreak: 'break-word',
                                }}
                              >
                                {txn.notes}
                              </span>
                            )}
                          </div>
                        ) : txn.status === 'Possible Match' ? (
                          <button
                            onClick={() => onReviewMatch(txn.id)}
                            style={{
                              padding: '4px 10px',
                              fontSize: '12px',
                              fontWeight: 600,
                              backgroundColor: '#fff3e0',
                              color: '#e65100',
                              border: '1px solid #ffb74d',
                              borderRadius: '4px',
                              cursor: 'pointer',
                            }}
                          >
                            🔍 Review
                          </button>
                        ) : null}
                        {/* Admin: Change Status button — always visible regardless of status */}
                        {isAdmin && onForceStatus && (
                          <button
                            onClick={() => setStatusModal({
                              txnId: txn.id,
                              currentStatus: txn.status as CCTransactionStatus,
                              selectedStatus: txn.status as CCTransactionStatus,
                              notes: '',
                              saving: false,
                            })}
                            title="Admin: force-set status"
                            style={{
                              marginTop: txn.status !== 'Coded' && txn.status !== 'Corrected' ? '0' : '4px',
                              padding: '3px 8px',
                              fontSize: '11px',
                              backgroundColor: '#f5f5f5',
                              color: '#555',
                              border: '1px solid #ccc',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              display: 'block',
                            }}
                          >
                            ⚙ Status
                          </button>
                        )}
                      </td>

                      {/* New Keyword */}
                      <td style={{ padding: '4px 10px' }}>
                        {locked ? (
                          <span style={{ color: '#888', fontSize: '12px' }}>{txn.suggested_keyword || ''}</span>
                        ) : (
                          <input
                            type="text"
                            value={draft.keyword}
                            onChange={e => handleFieldChange(txn.id, 'keyword', e.target.value)}
                            onBlur={() => handleBlur(txn, 'keyword')}
                            disabled={saving}
                            placeholder={txn.resolution_source === 'AI Assist' ? 'AI keyword' : ''}
                            style={{
                              width: '120px',
                              padding: '4px 6px',
                              border: '1px solid #cbd5e0',
                              borderRadius: '4px',
                              fontSize: '12px',
                              color: '#1a1a1a',
                              backgroundColor: draft.keyword ? '#f3e5f5' : 'white',
                            }}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>

              {/* Footer total row */}
              <tfoot>
                <tr style={{ backgroundColor: '#f5f7fa', borderTop: '2px solid #e0e0e0' }}>
                  <td /> {/* checkbox column */}
                  <td colSpan={6} style={{ padding: '7px 10px', fontWeight: 600, color: '#555', fontSize: '12px' }}>
                    {filtered.length} transaction{filtered.length !== 1 ? 's' : ''}
                    {activeTab !== 'All' && ` · ${activeTab}`}
                  </td>
                  <td style={{
                    padding: '7px 10px',
                    textAlign: 'right',
                    fontWeight: 700,
                    fontSize: '13px',
                    color: '#1a1a1a',
                    whiteSpace: 'nowrap',
                  }}>
                    {fmtAmt(tabTotal)}
                  </td>
                  <td colSpan={4} />
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}

      {/* Internal toast (inline-edit save failures only) */}
      {toast && (
        <div style={{
          position: 'fixed',
          bottom: '24px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: toast.ok ? '#2d7a2d' : '#c0392b',
          color: 'white',
          padding: '10px 20px',
          borderRadius: '6px',
          zIndex: 9999,
          fontSize: '14px',
          fontWeight: 500,
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          pointerEvents: 'none',
        }}>
          {toast.msg}
        </div>
      )}

      {/* Admin: Change Status modal */}
      {statusModal && (
        <div
          onClick={() => !statusModal.saving && setStatusModal(null)}
          style={{
            position: 'fixed', inset: 0,
            backgroundColor: 'rgba(0,0,0,0.45)',
            zIndex: 10000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '24px',
              width: '360px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            }}
          >
            <div style={{ fontWeight: 700, fontSize: '15px', marginBottom: '4px' }}>
              ⚙ Change Status
            </div>
            <div style={{ fontSize: '12px', color: '#888', marginBottom: '16px' }}>
              Current: <strong>{statusModal.currentStatus}</strong>
            </div>

            <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '6px' }}>
              New Status
            </label>
            <select
              value={statusModal.selectedStatus}
              onChange={e => setStatusModal(s => s ? { ...s, selectedStatus: e.target.value as CCTransactionStatus } : s)}
              disabled={statusModal.saving}
              style={{
                width: '100%', padding: '8px', fontSize: '13px',
                border: '1px solid #cbd5e0', borderRadius: '4px', marginBottom: '14px',
              }}
            >
              {(['Unresolved','Coded','Corrected','Possible Match','Confirmed Match','Converted','Duplicate','Error'] as CCTransactionStatus[]).map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>

            <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '6px' }}>
              Reason <span style={{ fontWeight: 400, color: '#999' }}>(optional)</span>
            </label>
            <input
              type="text"
              value={statusModal.notes}
              onChange={e => setStatusModal(s => s ? { ...s, notes: e.target.value } : s)}
              disabled={statusModal.saving}
              placeholder="e.g. Resetting stuck Error row"
              style={{
                width: '100%', padding: '8px', fontSize: '13px',
                border: '1px solid #cbd5e0', borderRadius: '4px',
                marginBottom: '20px', boxSizing: 'border-box',
              }}
            />

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setStatusModal(null)}
                disabled={statusModal.saving}
                style={{
                  padding: '8px 16px', fontSize: '13px', borderRadius: '4px',
                  border: '1px solid #ccc', backgroundColor: 'white', cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!onForceStatus || statusModal.saving) return;
                  setStatusModal(s => s ? { ...s, saving: true } : s);
                  try {
                    await onForceStatus(
                      statusModal.txnId,
                      statusModal.selectedStatus,
                      statusModal.notes || undefined,
                    );
                    setStatusModal(null);
                  } catch {
                    setStatusModal(s => s ? { ...s, saving: false } : s);
                  }
                }}
                disabled={statusModal.saving || statusModal.selectedStatus === statusModal.currentStatus}
                style={{
                  padding: '8px 18px', fontSize: '13px', fontWeight: 600,
                  borderRadius: '4px', border: 'none', cursor:
                    (statusModal.saving || statusModal.selectedStatus === statusModal.currentStatus)
                      ? 'default' : 'pointer',
                  backgroundColor:
                    (statusModal.saving || statusModal.selectedStatus === statusModal.currentStatus)
                      ? '#ccc' : '#1565c0',
                  color: 'white',
                }}
              >
                {statusModal.saving ? 'Saving…' : 'Apply'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
